/**
 * Pure validation / normalisation for the attendance-bands editor (WSRADM-0039).
 * Mirrors the API rules; gaps are fine; overlaps are not; sort by fromHour before send.
 */
import type { AttendanceBandDto } from '@/api/configuration/attendanceBands';

export interface BandDraft {
  id: string;
  label: string;
  fromHour: number | null;
  toHour: number | null;
}

export type BandIssue =
  | {
      kind:
        | 'label-blank'
        | 'label-long'
        | 'label-duplicate'
        | 'label-reserved'
        | 'hours-missing'
        | 'hours-order';
      index: number;
    }
  | { kind: 'overlap'; index: number; withIndex: number }
  | { kind: 'too-many' };

export const MAX_BANDS = 24;

export const HOUR_OPTIONS: readonly number[] = Array.from({ length: 25 }, (_, i) => i);

const RESERVED = new Set(['other', 'unknown time']);

let draftSeq = 0;

function nextDraftId(): string {
  draftSeq += 1;
  return `band-${draftSeq}`;
}

export function fromDto(bands: readonly AttendanceBandDto[]): BandDraft[] {
  return bands.map((band) => ({
    id: nextDraftId(),
    label: band.label,
    fromHour: band.fromHour,
    toHour: band.toHour,
  }));
}

export function toPutBody(drafts: readonly BandDraft[]): AttendanceBandDto[] {
  const body: AttendanceBandDto[] = [];
  for (const draft of drafts) {
    if (draft.fromHour === null || draft.toHour === null) continue;
    body.push({
      label: draft.label.trim(),
      fromHour: draft.fromHour,
      toHour: draft.toHour,
    });
  }
  body.sort((a, b) => a.fromHour - b.fromHour || a.toHour - b.toHour);
  return body;
}

function intervalsOverlap(
  aFrom: number,
  aTo: number,
  bFrom: number,
  bTo: number,
): boolean {
  return aFrom < bTo && bFrom < aTo;
}

export function validate(drafts: readonly BandDraft[]): BandIssue[] {
  const issues: BandIssue[] = [];
  if (drafts.length > MAX_BANDS) {
    issues.push({ kind: 'too-many' });
  }

  const labelIndex = new Map<string, number>();
  for (let i = 0; i < drafts.length; i++) {
    const draft = drafts[i]!;
    const trimmed = draft.label.trim();
    if (trimmed === '') {
      issues.push({ kind: 'label-blank', index: i });
    } else if (trimmed.length > 60) {
      issues.push({ kind: 'label-long', index: i });
    } else {
      const key = trimmed.toLowerCase();
      if (RESERVED.has(key)) {
        issues.push({ kind: 'label-reserved', index: i });
      }
      const prior = labelIndex.get(key);
      if (prior !== undefined) {
        issues.push({ kind: 'label-duplicate', index: i });
        // Flag the earlier row too so the UI highlights both.
        if (!issues.some((iss) => iss.kind === 'label-duplicate' && iss.index === prior)) {
          issues.push({ kind: 'label-duplicate', index: prior });
        }
      } else {
        labelIndex.set(key, i);
      }
    }

    if (draft.fromHour === null || draft.toHour === null) {
      issues.push({ kind: 'hours-missing', index: i });
    } else if (!(draft.fromHour < draft.toHour)) {
      issues.push({ kind: 'hours-order', index: i });
    }
  }

  for (let i = 0; i < drafts.length; i++) {
    const a = drafts[i]!;
    if (a.fromHour === null || a.toHour === null) continue;
    if (!(a.fromHour < a.toHour)) continue;
    for (let j = i + 1; j < drafts.length; j++) {
      const b = drafts[j]!;
      if (b.fromHour === null || b.toHour === null) continue;
      if (!(b.fromHour < b.toHour)) continue;
      if (intervalsOverlap(a.fromHour, a.toHour, b.fromHour, b.toHour)) {
        issues.push({ kind: 'overlap', index: i, withIndex: j });
        issues.push({ kind: 'overlap', index: j, withIndex: i });
      }
    }
  }

  return issues;
}

export function emptyDraft(): BandDraft {
  return { id: nextDraftId(), label: '', fromHour: null, toHour: null };
}

export function bandsEqual(
  a: readonly AttendanceBandDto[],
  b: readonly AttendanceBandDto[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (row, i) =>
      row.label === b[i]!.label &&
      row.fromHour === b[i]!.fromHour &&
      row.toHour === b[i]!.toHour,
  );
}
